/**
 * La gestion des stocks du magasin.
 *
 * *Demande de Chris, 07/09/2026 : le magasinier scanne un QR ou cherche un article, puis saisit
 * une entrée — livraison fournisseur ou retour chantier — ou une sortie.*
 *
 * ## Écrit pour le téléphone d'abord
 *
 * Comme « Saisir un OR », et pour la même raison : c'est un écran qu'on tient **debout**, dans un
 * rayonnage, parfois avec un gant. Une seule colonne, des cibles larges, rien qui déborde de
 * côté. Aucune media query — le layout part du plus petit écran, donc il n'a rien à défaire en
 * grand. Sur un écran de bureau il est simplement centré.
 *
 * ## Trois façons d'arriver sur un article, et une seule suite
 *
 * Le scan d'une étiquette, la recherche par code ou libellé, et l'arrivée par l'adresse du QR
 * (`/article/<code>`) quand on a photographié l'étiquette avec l'appareil photo du téléphone.
 * Les trois mènent au même formulaire : il n'y a qu'un chemin de saisie, donc une seule chose à
 * apprendre et un seul endroit où un défaut peut se cacher.
 *
 * ## Ce que l'écran montre, et pourquoi il le montre
 *
 * **Le stock et sa valeur au CMUP** avant la saisie, pas après : le magasinier doit voir ce
 * qu'il y a avant de dire ce qu'il enlève.
 *
 * **Le coût unitaire est prérempli depuis l'article** — la règle de Chris —, et il reste
 * modifiable : une livraison arrive parfois à un autre prix que celui de l'ERP.
 *
 * **Un stock négatif est affiché, jamais corrigé.** Une sortie plus grande que le stock passe :
 * le magasin a réellement sorti la marchandise, et un écran qui refuse mentirait sur ce qu'il y
 * a dans le rayonnage. Il le dit en rouge, ce qui est une information et non un blocage.
 *
 * **Les derniers mouvements sont sous le formulaire**, les plus récents d'abord : c'est ce qu'on
 * relit pour se corriger, et se corriger est le geste le plus fréquent après la saisie.
 *
 * **Les deux photos de la fiche se prennent d'ici** (demande de Chris du 07/09/2026). Le serveur
 * l'acceptait depuis la 067, mais aucun écran n'offrait le geste : le magasinier n'a pas accès à
 * l'écran d'administration des articles, qui était le seul à le proposer.
 *
 * **Tout le module vit ici, en onglets** — saisie, préparations, créations d'articles,
 * inventaire, état du stock. *Demande de Chris du 15/09/2026 : « on doit accéder à toutes les
 * fonctions par le menu Stock sur l'écran accueil ».* Les préparations et les créations étaient
 * jusque-là empilées sous la saisie ; l'inventaire aurait rendu la page interminable sur un
 * téléphone, et un onglet est aussi ce qui permet de dire d'un coup d'œil qu'il n'y a rien à
 * préparer. Côté application complète, ça évite surtout un bouton de plus dans une barre de menu
 * qui déborde déjà sous 1440 px.
 *
 * La seule fonction qui n'est pas ici est la **consultation du stock depuis une contre-étude** :
 * elle part d'une ligne de déboursé, c'est un autre geste et un autre écran (`StockDeLaLigne`).
 *
 * ## Le magasin (078)
 *
 * Chaque mouvement dit dans quel magasin il entre ou d'où il sort. Le champ est **caché quand il
 * n'y en a qu'un** — il est alors présélectionné, et une liste à une entrée est un clic pour rien.
 * Quand aucun n'est déclaré, l'écran le DIT et nomme le référentiel à ouvrir, au lieu d'arriver
 * muet comme la fonction que la 059 a dû reprendre.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import {
  chargerStockArticle,
  chercherArticles,
  chercherChantierParApo,
  deposerPhotoArticle,
  corrigerMouvementStock,
  poserEmplacement,
  poserMouvement,
  supprimerMouvement,
} from "./api";
import { DemandesArticles } from "./DemandesArticles";
import { codeDuScan, lireQrDansImage } from "./scan";
import { EtatDuStock } from "./EtatDuStock";
import { Inventaires } from "./Inventaires";
import { Preparations } from "./Preparations";
import type {
  ArticleTrouve,
  FicheStock,
  MouvementStock,
  OrigineEntree,
} from "./api";

const ORIGINES: readonly { readonly code: OrigineEntree; readonly libelle: string }[] = [
  { code: "livraison_fournisseur", libelle: "Livraison fournisseur" },
  { code: "retour_chantier", libelle: "Retour chantier" },
];

function formaterDate(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getFullYear())}`;
}

/** Un nombre décimal sans les zéros inutiles : « 12.000 » se lit « 12 ». */
function sansZeros(valeur: string): string {
  return valeur.includes(".") ? valeur.replace(/\.?0+$/, "") : valeur;
}

type Etat =
  | { readonly phase: "recherche" }
  | { readonly phase: "lecture" }
  | { readonly phase: "fiche"; readonly fiche: FicheStock }
  | { readonly phase: "erreur"; readonly raison: string };

type Onglet = "saisie" | "preparations" | "demandes" | "inventaires" | "etat";

export function Stocks(): JSX.Element {
  /**
   * L'onglet ouvert (15/09/2026).
   *
   * « Saisie » d'emblée : c'est le geste de tous les jours, et ce que le magasinier ouvre en
   * arrivant. Les autres onglets sont ce qu'on va chercher, pas ce qu'on fait.
   */
  const [onglet, setOnglet] = useState<Onglet>("saisie");
  const [etat, setEtat] = useState<Etat>({ phase: "recherche" });
  const [cherche, setCherche] = useState("");
  const [trouves, setTrouves] = useState<readonly ArticleTrouve[]>([]);
  const [messageScan, setMessageScan] = useState<string | null>(null);
  const champFichier = useRef<HTMLInputElement>(null);

  // La saisie en cours.
  const [sens, setSens] = useState<"entree" | "sortie">("entree");
  const [origine, setOrigine] = useState<OrigineEntree>("livraison_fournisseur");
  const [quantite, setQuantite] = useState("");
  const [cout, setCout] = useState("");
  const [remarque, setRemarque] = useState("");
  /**
   * Le magasin dans lequel on saisit (078).
   *
   * Gardé d'une saisie à l'autre : un magasinier travaille dans SON magasin, et le lui redemander
   * à chaque article serait un clic de plus à chaque ligne. Il est présélectionné quand il n'y en
   * a qu'un — ce qui est le cas aujourd'hui — et c'est l'écran qui le fait, jamais le serveur :
   * une valeur par défaut posée en base deviendrait fausse le jour où Chris en déclare un second,
   * et personne ne saurait quelles saisies en ont hérité.
   */
  const [site, setSite] = useState<string>("");
  /**
   * Le numéro d'affaire du chantier d'où revient la marchandise (079).
   *
   * *Demande de Chris, 15/09/2026.* Il est confirmé à la frappe : voir le nom du chantier
   * apparaître sous le champ est le seul contrôle possible sur un numéro recopié d'un bon de
   * livraison, et un numéro faux est refusé par le serveur plutôt qu'ignoré.
   */
  const [apo, setApo] = useState("");
  const [chantierTrouve, setChantierTrouve] = useState<string | null | "cherche">(null);
  /** L'adresse de rangement de l'article dans le magasin choisi, modifiable sur place. */
  const [emplacement, setEmplacement] = useState("");
  /**
   * Le mouvement qu'on CORRIGE, ou `null` quand on en pose un neuf (09/09/2026).
   *
   * *Question de Chris : « comment puis-je consulter le journal des mouvements du stock, y avoir
   * accès, éventuellement modifier si besoin ».* Le journal était consultable et un mouvement se
   * supprimait, mais **rien ne permettait de le corriger** : la route existait depuis la 066,
   * aucun écran ne l'appelait. Supprimer puis ressaisir n'est pas la même chose — le journal perd
   * alors la trace de ce qui a été corrigé, et une quantité mal tapée devient deux lignes.
   *
   * La correction reprend le formulaire de saisie plutôt que d'en ouvrir un second : un mouvement
   * se décrit à un seul endroit, sinon les deux finissent par ne plus accepter les mêmes choses.
   */
  const [corrige, setCorrige] = useState<MouvementStock | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [erreurSaisie, setErreurSaisie] = useState<string | null>(null);
  const [avis, setAvis] = useState<string | null>(null);
  const [photoEnCours, setPhotoEnCours] = useState<1 | 2 | null>(null);

  const ouvrirArticle = useCallback((articleId: string): void => {
    setEtat({ phase: "lecture" });
    chargerStockArticle(articleId)
      .then((fiche) => {
        setEtat({ phase: "fiche", fiche });
        /*
          Le coût de l'article, prérempli : c'est le prix à retenir (règle de Chris), et il
          reste modifiable.

          Débarrassé de ses zéros de queue : la colonne est un `numeric(14,5)`, donc la base
          rend « 42.50000 » là où le bon de livraison porte 42,50 €. Le champ affichait ces cinq
          décimales, et le magasinier qui corrigeait un prix devait d'abord les effacer.
        */
        setCout(sansZeros(fiche.article.coutUnitaire ?? ""));
        setQuantite("");
        setRemarque("");
        setErreurSaisie(null);
        /*
          Le magasin : gardé s'il est encore déclaré, présélectionné s'il n'y en a qu'un.

          Le repli sur « le seul magasin » n'est pas un défaut caché — il n'y a rien à choisir, et
          demander de choisir dans une liste à une entrée est un clic pour rien. Dès qu'il y en a
          deux, le champ reste vide et le serveur refuse une saisie muette.
        */
        setSite((actuel) => {
          if (fiche.sites.some((s) => s.id === actuel)) return actuel;
          return fiche.sites.length === 1 ? (fiche.sites[0]?.id ?? "") : "";
        });
      })
      .catch((erreur: unknown) => {
        setEtat({
          phase: "erreur",
          raison: erreur instanceof Error ? erreur.message : String(erreur),
        });
      });
  }, []);

  // La recherche, déclenchée à la frappe. Deux caractères au moins — en dessous, le serveur ne
  // cherche rien, et une liste tronquée au plafond serait arbitraire.
  useEffect(() => {
    const texte = cherche.trim();
    if (texte.length < 2) {
      setTrouves([]);
      return;
    }
    let abandonne = false;
    const minuterie = window.setTimeout(() => {
      chercherArticles(texte)
        .then((r) => {
          if (!abandonne) setTrouves(r.articles);
        })
        .catch(() => {
          if (!abandonne) setTrouves([]);
        });
    }, 250);
    return () => {
      abandonne = true;
      window.clearTimeout(minuterie);
    };
  }, [cherche]);

  /**
   * Le nom du chantier apparaît sous le champ dès qu'on tape un numéro d'affaire complet.
   *
   * C'est le seul contrôle possible sur un numéro recopié d'un bon de livraison : « APO250026 »
   * et « APO250036 » se ressemblent, et le magasinier n'a pas la liste des chantiers. Le serveur
   * refusera un numéro introuvable de toute façon — mais le refus arrive à l'enregistrement,
   * alors que le nom arrive pendant la frappe.
   */
  useEffect(() => {
    const texte = apo.trim();
    if (texte.length < 6 || !(sens === "entree" && origine === "retour_chantier")) {
      setChantierTrouve(null);
      return;
    }
    let abandonne = false;
    setChantierTrouve("cherche");
    const minuterie = window.setTimeout(() => {
      chercherChantierParApo(texte)
        .then((chantier) => {
          if (!abandonne) setChantierTrouve(chantier === null ? null : chantier.nom);
        })
        .catch(() => {
          if (!abandonne) setChantierTrouve(null);
        });
    }, 300);
    return () => {
      abandonne = true;
      window.clearTimeout(minuterie);
    };
  }, [apo, sens, origine]);

  /** L'adresse de rangement suit le magasin choisi : elle est différente d'un magasin à l'autre. */
  useEffect(() => {
    if (etat.phase !== "fiche") return;
    const part = etat.fiche.parSite.find((p) => p.siteId === site);
    setEmplacement(part?.emplacement ?? "");
  }, [etat, site]);

  const enregistrerLEmplacement = (): void => {
    if (etat.phase !== "fiche" || site === "") return;
    poserEmplacement(etat.fiche.article.id, site, emplacement.trim())
      .then(() => {
        setAvis(
          emplacement.trim() === ""
            ? "Adresse de rangement effacée."
            : `Rangé en « ${emplacement.trim()} ».`,
        );
        ouvrirArticle(etat.fiche.article.id);
      })
      .catch((erreur: unknown) => {
        setErreurSaisie(erreur instanceof Error ? erreur.message : String(erreur));
      });
  };

  /**
   * Ouvrir une fiche depuis un autre onglet ramène à la saisie.
   *
   * C'est le geste qui suit « ma demande d'article est validée » ou « cette préparation est
   * prête » : on veut la fiche de l'article, et elle vit dans l'onglet de saisie. Sans ce
   * changement d'onglet, le clic semblerait ne rien faire — la fiche se serait chargée dans un
   * onglet qu'on ne regarde pas.
   */
  const ouvrirDepuisUnOnglet = useCallback(
    (articleId: string): void => {
      setOnglet("saisie");
      ouvrirArticle(articleId);
    },
    [ouvrirArticle],
  );

  const scanner = (fichier: File): void => {
    setMessageScan("Lecture de l'étiquette…");
    lireQrDansImage(fichier)
      .then(async (scan) => {
        const code = scan === null ? null : codeDuScan(scan);
        if (code === null) {
          setMessageScan(
            "Étiquette illisible. Reprendre la photo de plus près, bien en face et sans reflet.",
          );
          return;
        }
        setMessageScan(null);
        // On passe par la recherche : elle rend l'identifiant, et le code lu est exact.
        const r = await chercherArticles(code);
        const exact = r.articles.find((a) => a.code === code);
        if (exact === undefined) {
          setMessageScan(`Aucun article ne porte le code « ${code} ».`);
          return;
        }
        setCherche("");
        setTrouves([]);
        ouvrirArticle(exact.id);
      })
      .catch((erreur: unknown) => {
        setMessageScan(erreur instanceof Error ? erreur.message : String(erreur));
      });
  };

  /** Repose le formulaire à vide : après un enregistrement, ou quand on renonce à corriger. */
  const reposerLaSaisie = (): void => {
    setCorrige(null);
    setSens("entree");
    setOrigine("livraison_fournisseur");
    setQuantite("");
    setCout("");
    setRemarque("");
    setApo("");
    setChantierTrouve(null);
    // Le magasin, lui, ne se repose PAS : on enchaîne les saisies dans le même magasin.
  };

  const enregistrer = (): void => {
    if (etat.phase !== "fiche") return;
    setEnvoi(true);
    setErreurSaisie(null);
    const articleId = etat.fiche.article.id;
    const unite = etat.fiche.article.unite ?? "";
    /*
      Le même formulaire, deux destinations.

      La correction envoie l'ENSEMBLE des champs et non ceux qui ont bougé : le serveur relit
      toute la saisie (`examinerLaSaisie`) pour la contrôler d'un bloc — un sens changé sans son
      origine serait une entrée sans provenance, et une contrainte de base le refuserait de toute
      façon. Le `survenuLe` d'origine est conservé : corriger une quantité ne déplace pas la date
      à laquelle le mouvement a eu lieu, et la valeur du stock à une date en dépend.
    */
    const saisie = {
      sens,
      origine: sens === "entree" ? origine : null,
      quantite: quantite.trim(),
      coutUnitaire: cout.trim() === "" ? undefined : cout.trim(),
      siteId: site,
      // Le numéro d'affaire ne voyage que sur un retour chantier : ailleurs il ne veut rien dire,
      // et l'envoyer quand même rattacherait une livraison fournisseur à un chantier.
      apo: sens === "entree" && origine === "retour_chantier" ? apo.trim() : null,
      remarque: remarque.trim() === "" ? null : remarque.trim(),
    };
    const envoye =
      corrige === null
        ? poserMouvement({ articleId, ...saisie })
        : corrigerMouvementStock(corrige.id, { ...saisie, survenuLe: corrige.survenuLe });

    envoye
      .then(() => {
        setAvis(
          corrige === null
            ? `${sens === "entree" ? "Entrée" : "Sortie"} de ${sansZeros(quantite.trim())} ` +
                `${unite} enregistrée.`
            : `Mouvement corrigé : ${sens === "entree" ? "entrée" : "sortie"} de ` +
                `${sansZeros(quantite.trim())} ${unite}. Le stock est recalculé.`,
        );
        reposerLaSaisie();
        ouvrirArticle(articleId);
      })
      .catch((erreur: unknown) => {
        setErreurSaisie(erreur instanceof Error ? erreur.message : String(erreur));
      })
      .finally(() => {
        setEnvoi(false);
      });
  };

  /** Charge un mouvement du journal dans le formulaire, pour le corriger. */
  const ouvrirEnCorrection = (mouvement: MouvementStock): void => {
    setCorrige(mouvement);
    setSens(mouvement.sens);
    // `inventaire` n'est pas une origine qu'on saisit : le serveur la conserve de lui-même sur
    // une régularisation qu'on corrige, et le formulaire n'a donc rien à en faire.
    if (mouvement.origine === "livraison_fournisseur" || mouvement.origine === "retour_chantier") {
      setOrigine(mouvement.origine);
    }
    // Le magasin du mouvement corrigé, et non celui où l'on travaillait : corriger une quantité
    // ne doit pas déplacer la marchandise d'un magasin à l'autre. Un mouvement d'avant la 078 n'en
    // porte pas — le champ reste alors vide et réclame son magasin, ce qui est le chemin de
    // réparation prévu pour les quatre mouvements d'essai.
    setSite(mouvement.siteId ?? "");
    setQuantite(mouvement.quantite);
    // Le coût RETENU et non celui stocké : sur une sortie, c'est celui que le mouvement a
    // réellement porté — le proposer autrement ferait corriger une quantité en déplaçant un prix.
    setCout(mouvement.coutRetenu ?? mouvement.coutUnitaire ?? "");
    setRemarque(mouvement.remarque ?? "");
    setErreurSaisie(null);
    setAvis(null);
  };

  const retirer = (mouvement: MouvementStock): void => {
    if (etat.phase !== "fiche") return;
    supprimerMouvement(mouvement.id)
      .then(() => {
        setAvis("Mouvement supprimé, le stock est recalculé.");
        reposerLaSaisie();
        ouvrirArticle(mouvement.articleId);
      })
      .catch((erreur: unknown) => {
        setErreurSaisie(erreur instanceof Error ? erreur.message : String(erreur));
      });
  };

  /**
   * La photo de l'article, prise depuis le rayonnage.
   *
   * *Demande de Chris, 07/09/2026 : « le magasinier doit aussi pouvoir prendre des photos des
   * articles et les mettre a jour dans la table article ».*
   *
   * Le serveur l'acceptait déjà — la route est ouverte au rôle `magasin` depuis la 067, bornée
   * aux deux emplacements de photo — mais **aucun écran ne lui offrait le geste** : le magasinier
   * n'a pas accès à l'écran d'administration des articles, qui était le seul à le proposer. Le
   * droit existait, la fonction n'arrivait nulle part.
   *
   * La fiche est **rechargée** après le dépôt plutôt que corrigée sur place : l'adresse de la
   * photo porte la date de modification de l'article, et c'est elle qui fait que le navigateur
   * cesse de servir l'ancienne image depuis son cache.
   */
  const deposerPhoto = (numero: 1 | 2, fichier: File): void => {
    if (etat.phase !== "fiche") return;
    const articleId = etat.fiche.article.id;
    setPhotoEnCours(numero);
    setErreurSaisie(null);
    deposerPhotoArticle(articleId, numero, fichier)
      .then(() => {
        setAvis(`Photo ${String(numero)} enregistrée.`);
        ouvrirArticle(articleId);
      })
      .catch((erreur: unknown) => {
        setErreurSaisie(erreur instanceof Error ? erreur.message : String(erreur));
      })
      .finally(() => {
        setPhotoEnCours(null);
      });
  };

  return (
    <section className="stocks">
      {/*
        TOUT LE MODULE EST ATTEIGNABLE D'ICI (demande de Chris, 15/09/2026 : « on doit accéder à
        toutes les fonctions par le menu Stock sur l'écran accueil »).

        Des onglets plutôt que des sections empilées : la page portait déjà la saisie, les
        préparations et les créations d'articles les unes sous les autres, et l'inventaire l'aurait
        rendue interminable sur un téléphone — l'écran se tient debout dans un rayonnage. Un onglet
        est aussi ce qui permet de dire d'un coup d'œil qu'il n'y a rien à préparer.

        La seule fonction qui NE vit pas ici est la consultation du stock depuis une contre-étude :
        elle part d'une ligne de déboursé, c'est un autre geste et un autre écran.
      */}
      <nav className="stocks__onglets">
        {(
          [
            ["saisie", "Saisie"],
            ["preparations", "À préparer"],
            ["demandes", "Créations d'articles"],
            ["inventaires", "Inventaire"],
            ["etat", "État du stock"],
          ] as const
        ).map(([code, libelle]) => (
          <button
            key={code}
            type="button"
            className={onglet === code ? "actif" : undefined}
            onClick={() => {
              setOnglet(code);
            }}
          >
            {libelle}
          </button>
        ))}
      </nav>

      {onglet === "preparations" && <Preparations onOuvrirArticle={ouvrirDepuisUnOnglet} />}
      {onglet === "demandes" && <DemandesArticles onOuvrirArticle={ouvrirDepuisUnOnglet} />}
      {onglet === "inventaires" && <Inventaires />}
      {onglet === "etat" && <EtatDuStock />}

      {onglet !== "saisie" ? null : (
        <>
      {/* Le scan et la recherche restent visibles au-dessus de la fiche : après une saisie, le
          geste suivant est presque toujours de passer à l'article d'à côté. */}
      <div className="stocks__acces">
        <label className="stocks__scan">
          <span>Scanner une étiquette</span>
          {/* `capture="environment"` ouvre l'appareil photo ARRIÈRE : le geste du magasinier
              devant son rayonnage. */}
          <input
            ref={champFichier}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => {
              const fichier = e.target.files?.[0];
              if (fichier !== undefined) scanner(fichier);
              e.target.value = "";
            }}
          />
        </label>

        <label className="stocks__cherche">
          <span>ou chercher par code ou libellé</span>
          <input
            value={cherche}
            placeholder="1000001, grille avaloir…"
            onChange={(e) => {
              setCherche(e.target.value);
            }}
          />
        </label>
      </div>

      {messageScan !== null && <p className="etat">{messageScan}</p>}

      {trouves.length > 0 && (
        <ul className="stocks__trouves">
          {trouves.map((a) => (
            <li key={a.id}>
              <button
                type="button"
                onClick={() => {
                  setCherche("");
                  setTrouves([]);
                  ouvrirArticle(a.id);
                }}
              >
                <strong>{a.code}</strong>
                <span>{a.description}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {etat.phase === "lecture" && <p>Chargement…</p>}
      {etat.phase === "erreur" && (
        <p className="alerte" role="alert">
          {etat.raison}
        </p>
      )}

      {etat.phase === "recherche" && trouves.length === 0 && cherche.trim() === "" && (
        <p className="etat">
          Scanner l'étiquette d'un article, ou le chercher par son code ou son libellé.
        </p>
      )}

      {etat.phase === "fiche" && (
        <>
          <article className="stocks__fiche">
            <h2>
              <span className="stocks__code">{etat.fiche.article.code}</span>
              {etat.fiche.article.description}
            </h2>

            {/* Le stock AVANT la saisie : on doit voir ce qu'il y a avant de dire ce qu'on
                enlève. */}
            <dl className={etat.fiche.etat.negatif ? "stocks__etat alerte" : "stocks__etat"}>
              <div>
                <dt>En stock</dt>
                <dd>
                  {sansZeros(etat.fiche.etat.quantite)} {etat.fiche.article.unite ?? ""}
                </dd>
              </div>
              <div>
                <dt>Coût moyen</dt>
                <dd>{sansZeros(etat.fiche.etat.cmup)} €</dd>
              </div>
              <div>
                <dt>Valeur</dt>
                <dd>{etat.fiche.etat.valeur} €</dd>
              </div>
            </dl>
            {etat.fiche.etat.negatif && (
              <p className="alerte" role="alert">
                Le stock est passé sous zéro. Ce n'est pas bloqué — il manque probablement une
                entrée à saisir.
              </p>
            )}

            {/*
              OÙ SE TROUVE CE STOCK (078).

              Affiché seulement quand il y a de quoi répartir : avec un seul magasin, la ligne
              répéterait le total juste au-dessus. Le coût moyen, lui, n'est pas repris par
              magasin — il n'y en a qu'un pour l'article (décision de Chris du 15/09/2026), et le
              montrer trois fois laisserait croire le contraire.
            */}
            {(etat.fiche.parSite.length > 1 || etat.fiche.sites.length > 1) && (
              <table className="stocks__magasins">
                <caption>Où se trouve ce stock</caption>
                <thead>
                  <tr>
                    <th>Magasin</th>
                    <th>Rangé en</th>
                    <th>Quantité</th>
                    <th>Valeur</th>
                  </tr>
                </thead>
                <tbody>
                  {etat.fiche.parSite.map((part) => (
                    <tr
                      key={part.siteId ?? "sans-site"}
                      className={part.siteId === null ? "stocks__sans-magasin" : undefined}
                    >
                      <td>{part.denomination}</td>
                      <td>{part.emplacement ?? "—"}</td>
                      <td className={Number(part.quantite) < 0 ? "alerte" : undefined}>
                        {sansZeros(part.quantite)} {etat.fiche.article.unite ?? ""}
                      </td>
                      <td>{part.valeur} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {etat.fiche.parSite.some((p) => p.siteId === null) && (
              <p className="stocks__note">
                « Site non désigné » : des mouvements saisis avant que les magasins existent. Les
                corriger dans le journal ci-dessous leur donnera leur magasin.
              </p>
            )}

            {/*
              OÙ CET ARTICLE EST RANGÉ, dans le magasin choisi (078).

              Une adresse, pas un compartiment de stock : le stock se compte par magasin, cette
              ligne dit seulement où aller le chercher. C'est elle qui ordonne la feuille
              d'inventaire, et c'est la raison de la saisir — parcourir un rayonnage dans l'ordre
              plutôt que de courir d'un bout à l'autre.

              Vidée, elle efface : la règle du 20/08/2026, qui évite deux façons de dire « je ne
              sais pas où il est ».
            */}
            {site !== "" && (
              <label className="stocks__rangement">
                Rangé en
                <input
                  value={emplacement}
                  placeholder="allée B / travée 3"
                  onChange={(e) => {
                    setEmplacement(e.target.value);
                  }}
                  onBlur={enregistrerLEmplacement}
                />
                <small>Vider le champ retire l'adresse.</small>
              </label>
            )}

            {/* Les deux emplacements de photo de la fiche (038), pris et remplacés d'ici : le
                magasinier a l'article dans les mains, et c'est le seul moment où la photo se
                prend. Un emplacement vide affiche son bouton, un emplacement rempli affiche
                l'image ET le bouton — remplacer est le geste courant, une première photo étant
                souvent floue. */}
            <div className="stocks__photos">
              {([1, 2] as const).map((numero) => (
                <div key={numero} className="stocks__emplacement">
                  {etat.fiche.article.photos[numero] !== null && (
                    <img
                      className="stocks__photo"
                      src={etat.fiche.article.photos[numero] ?? ""}
                      alt={`Photo ${String(numero)} de ${etat.fiche.article.description}`}
                    />
                  )}
                  <label className="stocks__prise">
                    <span>
                      {photoEnCours === numero
                        ? "Envoi…"
                        : etat.fiche.article.photos[numero] === null
                          ? `Prendre la photo ${String(numero)}`
                          : `Remplacer la photo ${String(numero)}`}
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      disabled={photoEnCours !== null}
                      onChange={(e) => {
                        const fichier = e.target.files?.[0];
                        if (fichier !== undefined) deposerPhoto(numero, fichier);
                        e.target.value = "";
                      }}
                    />
                  </label>
                </div>
              ))}
            </div>
          </article>

          <form
            className="stocks__saisie"
            onSubmit={(e) => {
              e.preventDefault();
              enregistrer();
            }}
          >
            <div className="stocks__sens">
              <button
                type="button"
                className={sens === "entree" ? "actif" : undefined}
                onClick={() => {
                  setSens("entree");
                }}
              >
                Entrée
              </button>
              <button
                type="button"
                className={sens === "sortie" ? "actif" : undefined}
                onClick={() => {
                  setSens("sortie");
                }}
              >
                Sortie
              </button>
            </div>

            {sens === "entree" && (
              <fieldset className="stocks__origine">
                <legend>D'où vient-elle ?</legend>
                {ORIGINES.map((o) => (
                  <label key={o.code}>
                    <input
                      type="radio"
                      name="origine"
                      checked={origine === o.code}
                      onChange={() => {
                        setOrigine(o.code);
                      }}
                    />
                    {o.libelle}
                  </label>
                ))}
              </fieldset>
            )}

            {/*
              LE NUMÉRO D'AFFAIRE D'UN RETOUR CHANTIER (079).

              *Demande de Chris, 15/09/2026.* Le nom du chantier s'affiche sous le champ dès qu'on
              a tapé assez de caractères : c'est le seul contrôle possible sur un numéro recopié
              d'un bon de livraison, « APO250026 » et « APO250036 » se ressemblant. Un numéro
              introuvable est refusé à l'enregistrement, jamais ignoré — une entrée silencieusement
              vidée de son chantier reviendrait de nulle part.
            */}
            {sens === "entree" && origine === "retour_chantier" && (
              <label>
                Numéro d'affaire du chantier
                <input
                  value={apo}
                  placeholder="APO250026"
                  autoCapitalize="characters"
                  onChange={(e) => {
                    setApo(e.target.value);
                  }}
                />
                {chantierTrouve === "cherche" && <small>Recherche…</small>}
                {chantierTrouve !== null && chantierTrouve !== "cherche" && (
                  <small className="stocks__chantier-trouve">{chantierTrouve}</small>
                )}
                {chantierTrouve === null && apo.trim().length >= 6 && (
                  <small className="alerte">Aucun chantier ne porte ce numéro.</small>
                )}
              </label>
            )}

            {/*
              LE MAGASIN (078).

              Caché quand il n'y en a qu'un — il est alors présélectionné, et une liste à une
              entrée est un clic pour rien. Dès qu'il y en a deux, il se choisit, et rien ne
              s'enregistre sans lui. Aucun magasin déclaré : on le DIT et on renvoie au
              référentiel, plutôt que d'afficher une liste vide dont personne ne comprendrait le
              silence.
            */}
            {etat.fiche.sites.length === 0 ? (
              <p className="alerte" role="alert">
                Aucun site de stockage n'est déclaré. Cochez « site de stockage » sur au moins un
                site dans Référentiels → Carrières et sites du groupe.
              </p>
            ) : (
              etat.fiche.sites.length > 1 && (
                <label>
                  Magasin
                  <select
                    value={site}
                    onChange={(e) => {
                      setSite(e.target.value);
                    }}
                  >
                    <option value="">— choisir —</option>
                    {etat.fiche.sites.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.denomination}
                      </option>
                    ))}
                  </select>
                </label>
              )
            )}

            <label>
              Quantité {etat.fiche.article.unite !== null && `(${etat.fiche.article.unite})`}
              <input
                // `decimal` et non `number` : sur un téléphone, il ouvre le pavé numérique avec
                // la virgule, et il n'ajoute pas les flèches minuscules d'un champ nombre.
                inputMode="decimal"
                value={quantite}
                autoFocus
                onChange={(e) => {
                  setQuantite(e.target.value);
                }}
              />
            </label>

            {/* Le prix ne se saisit QUE sur une entrée. Une sortie part au coût moyen du stock,
                et proposer un champ laisserait croire qu'on peut en décider — alors que le
                choix du CMUP l'interdit : sortir à un autre prix ferait dériver la valeur du
                stock sans que rien ne le dise. */}
            {sens === "entree" ? (
              <label>
                Coût unitaire (€)
                <input
                  inputMode="decimal"
                  value={cout}
                  onChange={(e) => {
                    setCout(e.target.value);
                  }}
                />
                <small>
                  Prérempli avec le coût de l'article. À corriger si la livraison arrive à un
                  autre prix.
                </small>
              </label>
            ) : (
              <p className="etat">
                La sortie est valorisée au coût moyen du stock,{" "}
                <strong>{sansZeros(etat.fiche.etat.cmup)} €</strong> — rien à saisir.
              </p>
            )}

            <label>
              Remarque
              <input
                value={remarque}
                onChange={(e) => {
                  setRemarque(e.target.value);
                }}
              />
            </label>

            <button type="submit" disabled={envoi || quantite.trim() === "" || site === ""}>
              {envoi
                ? "Enregistrement…"
                : corrige !== null
                  ? "Enregistrer la correction"
                  : sens === "entree"
                    ? "Entrer en stock"
                    : "Sortir du stock"}
            </button>

            {/* On DIT qu'on corrige, et on offre d'y renoncer. Sans cela, le formulaire prérempli
                se lit comme une nouvelle saisie, et enregistrer doublerait le mouvement dans la
                tête de celui qui le fait — même si le serveur, lui, corrige bien. */}
            {corrige !== null && (
              <p className="etat">
                Correction du mouvement du {formaterDate(corrige.survenuLe)} — la date reste
                celle-là.{" "}
                <button
                  type="button"
                  className="lien"
                  onClick={() => {
                    reposerLaSaisie();
                  }}
                >
                  Renoncer
                </button>
              </p>
            )}

            {erreurSaisie !== null && (
              <p className="alerte" role="alert">
                {erreurSaisie}
              </p>
            )}
            {avis !== null && <p className="etat">{avis}</p>}
          </form>

          {etat.fiche.mouvements.length > 0 && (
            <section className="stocks__mouvements">
              <h3>Derniers mouvements</h3>
              <ul>
                {etat.fiche.mouvements.slice(0, 20).map((m) => (
                  <li key={m.id}>
                    <span className={m.sens === "entree" ? "stocks__entree" : "stocks__sortie"}>
                      {m.sens === "entree" ? "+" : "−"}
                      {sansZeros(m.quantite)}
                    </span>
                    <span className="stocks__quand">{formaterDate(m.survenuLe)}</span>
                    <span className="stocks__quoi">
                      {/* `originesLues` et non `origines` : une régularisation d'inventaire ne se
                          saisit pas, mais elle se lit — sans elle, le journal montrerait des
                          lignes dont il ne sait pas dire d'où elles viennent. */}
                      {m.origine === null
                        ? "Sortie"
                        : (etat.fiche.originesLues[m.origine] ?? m.origine)}
                      {/* Le magasin, seulement quand il y en a plusieurs à distinguer — ou quand
                          il manque, ce qui est une chose à réparer et non un détail. */}
                      {m.siteId === null
                        ? " — site non désigné"
                        : etat.fiche.sites.length > 1 &&
                          ` — ${etat.fiche.sites.find((s) => s.id === m.siteId)?.denomination ?? ""}`}
                      {m.remarque !== null && ` — ${m.remarque}`}
                    </span>
                    {/* Le coût RETENU, pas celui stocké : sur une sortie, c'était le coût de la
                        fiche article, et la ligne annonçait un prix que ces unités n'ont jamais
                        porté. */}
                    <span className="stocks__prix">
                      {sansZeros(m.coutRetenu ?? m.coutUnitaire)} €
                    </span>
                    {/* CORRIGER plutôt que supprimer-ressaisir (09/09/2026) : deux lignes pour
                        une quantité mal tapée, c'est un journal qui ne dit plus ce qui s'est
                        passé. La ligne remonte dans le formulaire ci-dessus. */}
                    <button
                      type="button"
                      className="lien"
                      onClick={() => {
                        ouvrirEnCorrection(m);
                      }}
                    >
                      Corriger
                    </button>
                    <button
                      type="button"
                      className="lien"
                      onClick={() => {
                        retirer(m);
                      }}
                    >
                      Supprimer
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

        </>
      )}
    </section>
  );
}
